<template>
    <v-form v-on:submit.prevent="submitFom()" ref="form" v-model="valid">
        <v-card>
            <v-toolbar class="custom-bg" density="compact">
                <v-toolbar-title>
                    Opdrachtgever-specifieke tarieven en kortingen
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <h3>Standaard kortingspercentages</h3>
                <p class="mt-2">Deze kortingspercentages gelden voor alle artikelnummers in de groep, tenzij overschreven op artikelniveau.</p>

                <table class="mt-2">
                    <tbody>
                        <tr>
                            <td>Materiaal</td>
                            <td>
                                <v-text-field
                                    v-model="client.equipment_discount_perc"
                                    hide-details
                                    class="text-right percentage-input"
                                    density="compact"
                                    append-inner-icon="mdi-percent"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Beletteringskosten</td>
                            <td>
                                <v-text-field
                                    v-model="client.lettering_discount_perc"
                                    hide-details
                                    class="text-right percentage-input"
                                    density="compact"
                                    append-inner-icon="mdi-percent"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Programmeer- en datakosten</td>
                            <td>
                                <v-text-field
                                    v-model="client.progr_data_discount_perc"
                                    hide-details
                                    class="text-right percentage-input"
                                    density="compact"
                                    append-inner-icon="mdi-percent"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Werkzaamheden</td>
                            <td>
                                <v-text-field
                                    v-model="client.work_order_hour_discount_perc"
                                    hide-details
                                    class="text-right percentage-input"
                                    density="compact"
                                    append-inner-icon="mdi-percent"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h3 class="mt-4">Aangepaste tarieven en kortingspercentage per artikel</h3>
                <p class="mt-2">Via onderstaande velden kunnen standaard tarieven en kortingspercentages worden overschreven voor specifieke interne artikelnummers.</p>
                <p class="mt-2">Een aangepast tarief verschijnt niet als korting, maar als nieuw tarief. Op een aangepast tarief kan ook een kortingspercentage worden toegepast. In dat geval wordt de korting berekend over het aangepaste tarief.</p>
                <div v-if="internalArticlesGroupedByType && Object.keys(internalArticlesGroupedByType).length">
                    <table>
                        <template v-for="(articles, type) in internalArticlesGroupedByType" :key="type">
                            <thead>
                                <tr>
                                    <th colspan="7">
                                        <h3 class="mt-2 font-normal">
                                            <template v-if="type === 'equipment'">Materiaal</template>
                                            <template v-else-if="type === 'lettering_cost'">Beletteringskosten</template>
                                            <template v-else-if="type === 'progr_data_cost'">Programmeer- en datakosten</template>
                                            <template v-else-if="type === 'hour'">Werkzaamheden</template>
                                            <template v-else>{{ type }}</template>
                                        </h3>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Intern artikelnummer</th>
                                    <th>Omschrijving</th>
                                    <th>Tarief</th>
                                    <th colspan="2">Tarief</th>
                                    <th>Korting percentage</th>
                                    <th>Korting percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="article in articles" :key="article.id">
                                    <td>{{ article.internal_article_number }}</td>
                                    <td>{{ article.internal_article_number_description }}</td>
                                    <td>
                                        <v-text-field
                                            v-model="article.rate_excl_vat"
                                            hide-details
                                            class="text-right amount-input"
                                            density="compact"
                                            type="number"
                                            min="0"
                                            prepend-inner-icon="mdi-currency-eur"
                                        />
                                    </td>
                                    <td class="no-wrap">
                                        {{ calculateRate(article) }}
                                    </td>
                                    <td class="text-small">
                                        {{ article.rate_type ? article.rate_type : '' }}
                                    </td>
                                    <td>
                                        <v-text-field
                                            v-model="article.discount_percentage"
                                            hide-details
                                            class="text-right percentage-input"
                                            density="compact"
                                            append-inner-icon="mdi-percent"
                                        />
                                    </td>
                                    <td>
                                        {{ calculateDiscount(article) }}
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </table>
                </div>
                <div v-else>
                    <p>Geen interne artikelen gevonden.</p>
                </div>
            </v-card-text>
            <v-card-actions v-if="internalArticlesGroupedByType && Object.keys(internalArticlesGroupedByType).length">
                <v-btn color="orange-darken-2" depressed type="submit" v-show="!formSubmitted" :disabled="!valid || isArchived">
                    Opslaan
                </v-btn>
                <v-progress-circular indeterminate color="orange-darken-2" v-show="formSubmitted"></v-progress-circular>
                <div v-if="isArchived" class="text-red ml-2">
                    {{ $t('clientIsArchived') }}
                </div>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script setup lang="ts">
import { fetchInternalArticles } from '@/api/internalArticles';
import SayErrorResponse from "@/mixins/sayErrorResponse";
import { onMounted, ref, computed } from "vue";
import { updateCustomRatesAndDiscounts } from '@/api/clients';
import Say from '@/utils/Say';

const props = defineProps({
  client: {
    type: Object,
    required: true
  }
})

const formSubmitted = ref(false);
const valid = ref(false);
const internalArticles = ref([]);
const isArchived = computed(() => props.client?.id && props.client.deleted_at !== null)

const loadInternalArticles = async () => {
  try {
    const response = await fetchInternalArticles();
    if(!response.data) {
      SayErrorResponse(response);
    } else {
        internalArticles.value = response.data;
    }
  } catch (error) {
    SayErrorResponse(error);
  }
}

onMounted(() => {
  loadInternalArticles();
});

const internalArticlesGroupedByType = computed(() => {
  return internalArticles.value.reduce((groups, article) => {
    const type = article.type || 'Unknown';
    if(type === 'Unknown' || type === 'additional_internal_article' || type === 'work_order_category') {
        return groups;
    }
    if (!groups[type]) {
      groups[type] = [];
    }
    article.rate_excl_vat = props.client.internal_articles?.find((a) => a.internal_article_number === article.internal_article_number)?.rate_excl_vat;
    article.discount_percentage = props.client.internal_articles?.find((a) => a.internal_article_number === article.internal_article_number)?.discount_percentage;
    groups[type].push(article);
    return groups;
  }, {});
});

const calculateDiscount = (article: any) => {
    let discountPercentage = 0;
    if(article.type === 'equipment') {
        discountPercentage = props.client.equipment_discount_perc || 0;
    } else if(article.type === 'lettering_cost') {
        discountPercentage = props.client.lettering_discount_perc || 0;
    } else if(article.type === 'progr_data_cost') {
        discountPercentage = props.client.progr_data_discount_perc || 0;
    } else if(article.type === 'hour') {
        discountPercentage = props.client.work_order_hour_discount_perc || 0;
    }

    if(
        article.discount_percentage !== '' &&
        article.discount_percentage !== null &&
        article.discount_percentage !== undefined &&
        article.discount_percentage >= 0 &&
        article.discount_percentage <= 100
    ) {
        discountPercentage = article.discount_percentage;
    }

    return discountPercentage && discountPercentage != 0 ? discountPercentage + '%' : '-';
}

const calculateRate = (article: any) => {
    const rate = article.rate !== '' && article.rate !== null && article.rate !== undefined ? article.rate : '-';

    if(article.rate_excl_vat !== '' && article.rate_excl_vat !== null && article.rate_excl_vat !== undefined && article.rate_excl_vat >= 0) {
        return '€ ' + formatCurrency(article.rate_excl_vat);
    }

    return rate ? '€ ' + formatCurrency(rate) : '-';
}

const formattedFormData = computed(() => {
    const data = {
        equipment_discount_perc: props.client.equipment_discount_perc,
        lettering_discount_perc: props.client.lettering_discount_perc,
        progr_data_discount_perc: props.client.progr_data_discount_perc,
        work_order_hour_discount_perc: props.client.work_order_hour_discount_perc,
        internal_articles: Object.values(internalArticlesGroupedByType.value).flat().map(article => ({
            internal_article_number: article.internal_article_number,
            rate_excl_vat: article.rate_excl_vat,
            discount_percentage: article.discount_percentage
        }))
    }
    return data;
})

const formatCurrency = (value: number) => Number(value).toLocaleString('nl-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const submitFom = () => {
    formSubmitted.value = true

    updateCustomRatesAndDiscounts(props.client.id, formattedFormData.value)
        .then((response: { data: { error: any; }; }) => {
            if (response.data.error) {
                SayErrorResponse(response);
            } else {
                Say('success', 'Opdrachtgever-specifieke tarieven en kortingen zijn opgeslagen');
            }
        })
        .catch((error: any) => {
            SayErrorResponse(error);
        })
        .finally(() => {
            formSubmitted.value = false
        });
}
</script>

<style scoped>
th {
    text-align: left;
    min-width: 100px;
}
th,
td {
    padding: 2px 12px 2px 0;
    border-bottom: 1px solid #ddd;
}
.font-normal {
    font-weight: normal;
}
:deep(.v-input.amount-input input),
:deep(.v-input.percentage-input input) {
  padding-top: 0;
  padding-bottom: 0;
  min-height: 28px;
  min-width: 100px;
}
.no-wrap {
  white-space: nowrap;
}
.text-small {
    font-size: 0.825rem;
}
</style>